/**
 * Pure helpers for the report-delete cascade — Workflow Prompt 7.
 * Tested in src/lib/__tests__/reportDeleteCascade.test.ts.
 */
import type { SiteRagReport, SiteDocument } from '../types/siteRag';

export type DeleteMode = 'report-only' | 'with-evidence' | 'with-everything';

export interface DeleteImpact {
  /** Human-readable mode label for the toast / dialog. */
  modeLabel: string;
  /** Whether the SED file is eligible to be removed (no other reports reference it). */
  canRemoveSed: boolean;
  /** Reason canRemoveSed is false (e.g. "Referenced by 2 other report(s) for this site"). */
  sedBlockReason?: string;
  /** Documents that would be removed in 'with-everything' mode. */
  documentsToRemove: SiteDocument[];
  /** Total bytes that would be freed (report payload approximate + SED + docs). */
  bytesFreed: number;
}

/**
 * Returns true if any report in `allReports` (other than the one being deleted)
 * references the same `sedId`. Used to gate SED removal — we never delete a
 * SED that's still in use.
 */
export function isSedStillReferenced(reportToDelete: SiteRagReport, allReports: SiteRagReport[]): { stillReferenced: boolean; referencingReportIds: string[] } {
  if (!reportToDelete.sedId) return { stillReferenced: false, referencingReportIds: [] };
  const others = allReports.filter(r => r.id !== reportToDelete.id && r.sedId === reportToDelete.sedId);
  return { stillReferenced: others.length > 0, referencingReportIds: others.map(r => r.id) };
}

/**
 * Compute what would be removed if the user confirmed delete with `mode`.
 * Pure; doesn't perform any IO. Used to populate the confirmation dialog
 * and the post-delete toast.
 *
 * `reportPayloadBytes` is an estimate — we don't know GCS object size until
 * we'd HEAD the file, but for the dialog the doc-sum is the most meaningful
 * number. Caller can pass 0 if unknown.
 */
export function computeDeleteImpact(
  reportToDelete: SiteRagReport,
  allReports: SiteRagReport[],
  mode: DeleteMode,
  reportPayloadBytes = 0,
): DeleteImpact {
  const sedRef = isSedStillReferenced(reportToDelete, allReports);
  const canRemoveSed = !sedRef.stillReferenced && !!reportToDelete.sedId;
  const sedBlockReason = sedRef.stillReferenced
    ? `Referenced by ${sedRef.referencingReportIds.length} other report(s) for this site — SED kept`
    : undefined;

  const docsToRemove = mode === 'with-everything' ? (reportToDelete.documents ?? []) : [];
  const docBytes = docsToRemove.reduce((s, d) => s + (d.sizeBytes || 0), 0);

  // SED file: rough guess if we don't know size — assume 50KB average for a SED JSON.
  // (We can't HEAD the file from the pure helper; the live store action could refine.)
  const sedBytes = (mode === 'with-evidence' || mode === 'with-everything') && canRemoveSed ? 50_000 : 0;

  return {
    modeLabel:
      mode === 'report-only' ? 'Report only' :
      mode === 'with-evidence' ? 'Report + evidence (SED)' :
      'Report + evidence + uploaded documents',
    canRemoveSed,
    sedBlockReason,
    documentsToRemove: docsToRemove,
    bytesFreed: reportPayloadBytes + sedBytes + docBytes,
  };
}

/** Format bytes for human-readable display in the dialog/toast. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
